/**
 * usePullToRefresh
 *
 * Geste "tirer vers le bas depuis le haut" -> appelle onRefresh().
 *
 * Usage (API inchangée) :
 *   const { pullDistance, isRefreshing, threshold, bindProps } = usePullToRefresh(loadData);
 *   <div {...bindProps}>
 *     <PullToRefreshIndicator distance={pullDistance} threshold={threshold} loading={isRefreshing} />
 *     ...content...
 *   </div>
 *
 * Implémentation robuste : listener `touchmove` NON-PASSIF sur `document` (les props
 * React sont passives -> preventDefault impossible -> le WebView fait son rebond natif
 * et avale le geste). Ici on preventDefault pendant un tirage-vers-le-bas-en-haut ->
 * le rebond natif est bloqué, notre indicateur s'affiche, le refresh part au relâché.
 * Marche quelle que soit la version de Capacitor/WebView. Ne bloque JAMAIS le scroll
 * normal ni les swipes horizontaux (garde-fou vertical-dominant).
 */
import { useRef, useState, useEffect } from 'react';

const THRESHOLD = 70;      // distance px avant de déclencher
const MAX_PULL = 120;      // distance max affichée (frein élastique)

export function usePullToRefresh(onRefresh) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startY = useRef(null);
  const startX = useRef(null);
  const tracking = useRef(false);
  const pull = useRef(0);
  const refreshing = useRef(false);
  const cb = useRef(onRefresh);
  cb.current = onRefresh;                 // toujours le dernier loadData, sans ré-attacher

  useEffect(() => {
    const atTop = (startEl) => {
      let el = startEl;
      while (el && el.nodeType === 1) {
        if (el.scrollHeight > el.clientHeight + 1) {
          const oy = getComputedStyle(el).overflowY;
          if (oy === 'auto' || oy === 'scroll') return el.scrollTop <= 0;
        }
        el = el.parentElement;
      }
      const se = document.scrollingElement || document.documentElement;
      return ((window.scrollY || 0) || (se && se.scrollTop) || (document.body && document.body.scrollTop) || 0) <= 0;
    };

    const onStart = (e) => {
      if (refreshing.current || !e.touches || e.touches.length !== 1) { tracking.current = false; return; }
      if (!atTop(e.target)) { tracking.current = false; return; }
      startY.current = e.touches[0].clientY;
      startX.current = e.touches[0].clientX;
      tracking.current = true;
      pull.current = 0;
    };

    const onMove = (e) => {
      if (!tracking.current || startY.current == null || refreshing.current) return;
      const dy = e.touches[0].clientY - startY.current;
      const dx = e.touches[0].clientX - startX.current;
      if (dy <= 0) { pull.current = 0; setPullDistance(0); return; }                    // vers le haut -> rien
      if (Math.abs(dx) > dy) { tracking.current = false; pull.current = 0; setPullDistance(0); return; }  // geste horizontal -> scroll natif
      if (!atTop(e.target)) { tracking.current = false; pull.current = 0; setPullDistance(0); return; }
      const damped = dy < THRESHOLD ? dy : THRESHOLD + (dy - THRESHOLD) * 0.4;
      pull.current = Math.min(damped, MAX_PULL);
      setPullDistance(pull.current);
      if (e.cancelable) e.preventDefault();   // bloque le rebond natif -> notre indicateur s'affiche
    };

    const onEnd = async () => {
      if (!tracking.current) return;
      tracking.current = false;
      startY.current = null;
      const triggered = pull.current >= THRESHOLD;
      pull.current = 0;
      if (triggered && !refreshing.current) {
        refreshing.current = true;
        setIsRefreshing(true);
        try { await cb.current(); } catch (err) { console.error('pull-to-refresh:', err); }
        try { window.dispatchEvent(new Event('curio:refresh-badges')); } catch (e) {}   // maj aussi les pastilles navbar
        refreshing.current = false;
        setIsRefreshing(false);
      }
      setPullDistance(0);
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });   // NON-passif = clé
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  return {
    pullDistance,
    isRefreshing,
    threshold: THRESHOLD,
    bindProps: {},   // listeners sur document -> rien à poser sur l'élément
  };
}
